import React, { useEffect, useRef } from 'react';
import Masonry from 'masonry-layout';
import PostEngagementWidget from '../../components/BusinessDashboard/PostEngagementWidget';
import FollowersWidget from '../../components/BusinessDashboard/FollowersWidget';
import PostQuotaWidget from '../../components/BusinessDashboard/PostQuotaWidget';
import PerformanceWidget from '../../components/BusinessDashboard/PerformanceWidget';
import PostCoverageMapWidget from '../../components/BusinessDashboard/PostCoverageMapWidget';
import BusinessProfileWidget from '../../components/BusinessDashboard/BusinessProfileWidget';
import TopPerformingPostWidget from '../../components/BusinessDashboard/TopPerformingPostWidget';
import PostRefreshReminderWidget from '../../components/BusinessDashboard/PostRefreshReminderWidget';
import PendingTasksWidget from '../../components/BusinessDashboard/PendingTasksWidget';
import ActivityFeedWidget from '../../components/BusinessDashboard/ActivityFeedWidget';
import PaymentWidget from '../../components/BusinessDashboard/PaymentWidget';
import LastLoginWidget from '../../components/BusinessDashboard/LastLoginWidget';
import BusinessProfileWidgetBasic from '../../components/BusinessDashboard/BusinessProfileWidgetBasic';
import PostEngagementWidgetBasic from '../../components/BusinessDashboard/PostEngagementWidgetBasic';
import TopPerformingPostWidgetBasic from '../../components/BusinessDashboard/TopPerformingPostWidgetBasic';
import PostRefreshReminderWidgetBasic from '../../components/BusinessDashboard/PostRefreshReminderWidgetBasic';
import PendingTasksWidgetBasic from '../../components/BusinessDashboard/PendingTasksWidgetBasic';
import ActivityFeedWidgetBasic from '../../components/BusinessDashboard/ActivityFeedWidgetBasic';
import LastLoginWidgetBasic from '../../components/BusinessDashboard/LastLoginWidgetBasic';
import PerformanceWidgetBasic from '../../components/BusinessDashboard/PerformanceWidgetBasic';
import useAuth from '../../Hooks/useAuth';

const Dashboard = () => {
  const { auth } = useAuth();
  const masonryGridRef = useRef(null);

  const isBasicPlan = auth?.userPlan?.plan?.name?.toLowerCase() === 'basic';
  const isProPlan = auth?.userPlan?.plan?.name?.toLowerCase() === 'pro';

  useEffect(() => {
    if (!masonryGridRef.current || typeof Masonry === 'undefined') return undefined;

    let masonry;
    let resizeHandler;

    const images = masonryGridRef.current.querySelectorAll('img');
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    });

    Promise.all(imagePromises)
      .then(() => {
        if (!masonryGridRef.current) return;
        try {
          masonry = new Masonry(masonryGridRef.current, {
            itemSelector: '.masonry-item',
            columnWidth: 10,
            gutter: 0,
          });
          masonryGridRef.current.masonry = masonry;
          resizeHandler = () => masonry?.layout?.();
          window.addEventListener('resize', resizeHandler);
        } catch (error) {
          console.warn('Failed to initialize Masonry:', error);
        }
      })
      .catch((error) => {
        console.warn('Failed to load images for Masonry:', error);
      });

    return () => {
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (masonryGridRef.current?.masonry) {
        masonryGridRef.current.masonry.destroy();
        masonryGridRef.current.masonry = null;
      }
    };
  }, [isBasicPlan, isProPlan]);

  return (
    <div ref={masonryGridRef} className="masonry-grid">
      {isProPlan && (
        <>
          <PostCoverageMapWidget />
          <BusinessProfileWidget />
          <FollowersWidget />
          <PostEngagementWidget />
          <PostQuotaWidget />
          <TopPerformingPostWidget />
          <PerformanceWidget />
          <PostRefreshReminderWidget />
          <PendingTasksWidget />
          <ActivityFeedWidget />
          <PaymentWidget />
          <LastLoginWidget />
        </>
      )}

      {isBasicPlan && (
        <>
          <BusinessProfileWidgetBasic />
          <PostEngagementWidgetBasic />
          <PerformanceWidgetBasic />
          <PendingTasksWidgetBasic />
          <ActivityFeedWidgetBasic />
          <PostRefreshReminderWidgetBasic />
          <TopPerformingPostWidgetBasic />
          <LastLoginWidgetBasic />
        </>
      )}
    </div>
  );
};

export default Dashboard;
